import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { constants as coreConstants } from '@wsb/guac-widget-core';
import { INTERNAL } from '../constants/blog';
import { hasPostUrl } from '../helpers/blogPathHelper';
import {
  buildPostPath,
  getPagePath,
  getFriendlySlug,
  getAllPostsParam,
  getSlugParam,
  pathContainsFriendlySlug,
  setAllPostsParam,
  setSlugParam
} from '../helpers/blogPathHelper';
import { getCategoryParam, setCategoryParam } from '../helpers/categoryHelpers';
import { isSame, existsAndIsSame } from '../helpers/comparisonHelpers';
import { shouldRenderWidget } from '../helpers/disqus';

const { PUBLISH } = coreConstants.renderModes;

const hasNoWindow = () => typeof window === 'undefined';

class Router extends Component {
  static get propTypes() {
    return {
      env: PropTypes.string,
      websiteId: PropTypes.string.isRequired,
      pageId: PropTypes.string,
      locale: PropTypes.string,
      section: PropTypes.string,
      category: PropTypes.string,
      renderMode: PropTypes.oneOf(Object.values(coreConstants.renderModes)).isRequired,
      viewDevice: PropTypes.oneOf(Object.values(coreConstants.renderDevices)).isRequired,
      id: PropTypes.string,
      rssFeed: PropTypes.string,
      categoriesFilter: PropTypes.array,
      blogType: PropTypes.string.isRequired,
      layout: PropTypes.string.isRequired,
      staticContent: PropTypes.object.isRequired,
      backgroundImage: PropTypes.string,
      visitorMode: PropTypes.bool.isRequired,
      detachedMode: PropTypes.bool.isRequired,
      currentPageRoute: PropTypes.string
    };
  }

  constructor() {
    super(...arguments);

    this.state = {};
    this.filterCategory = this.filterCategory.bind(this);
    this.navigate = this.navigate.bind(this);
    this.updateStateFromLocation = this.updateStateFromLocation.bind(this);
    this.scrollIntoView = this.scrollIntoView.bind(this);
    this.waitForComments = this.waitForComments.bind(this);
  }

  scrollIntoView(nextState) {
    const { id } = this.props;

    if (this.shouldScrollIntoView(nextState) && typeof document !== 'undefined') {
      if (hasPostUrl(window) && shouldRenderWidget(window._BLOG_DATA)) this.waitForComments();
      else document.getElementById(id).scrollIntoView();
    }
  }

  shouldScrollIntoView(nextState) {
    const { renderMode } = this.props;
    if (renderMode !== PUBLISH) return false;

    const { slug, selectedCategory } = nextState || this.state;
    const isAllPosts = getAllPostsParam();

    return !!(slug || selectedCategory || isAllPosts || hasPostUrl(window));
  }

  waitForComments() {
    const commentsContainer = document.getElementById('disqus_thread');
    if (commentsContainer) commentsContainer.scrollIntoView();
    else requestAnimationFrame(this.waitForComments);
  }

  componentDidMount() {
    const nextState = {
      slug: this.getSlug(),
      selectedCategory: this.getSelectedCategory()
    };

    if (nextState.slug) {
      this.setState({
        ...nextState,
        previousScrollPosition: window.scrollY
      });
    } else if (nextState.selectedCategory) {
      this.setState(nextState);
    }

    this.scrollIntoView(nextState);

    window.addEventListener('popstate', this.updateStateFromLocation);
    if (this.props.renderMode !== PUBLISH) {
      window.addEventListener('blog-navigate', this.updateStateFromLocation);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('popstate', this.updateStateFromLocation);
    window.removeEventListener('blog-navigate', this.updateStateFromLocation);
  }

  componentDidUpdate(prevProps) {
    if (this.props.renderMode !== prevProps.renderMode) {
      this.updateStateFromLocation();
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (
      this.props.blogType === nextProps.blogType &&
      nextProps.blogType === INTERNAL &&
      nextProps.renderMode !== 'EDIT'
    ) {
      if (
        existsAndIsSame(this.state, nextState, ['slug']) &&
        isSame(this.state, nextState, ['selectedCategory'])
      ) {
        return false;
      }
    }
    return true;
  }

  updateStateFromLocation() {
    this.setState({ slug: this.getSlug(), selectedCategory: this.getSelectedCategory() });
  }

  getSlug() {
    if (hasNoWindow()) return;

    if (this.props.renderMode === PUBLISH && pathContainsFriendlySlug()) {
      return getFriendlySlug();
    }

    return getSlugParam();
  }

  getSelectedCategory() {
    if (hasNoWindow()) return;
    return getCategoryParam();
  }

  generateNavigateEvent() {
    const eventName = 'blog-navigate';
    if (window.Event.prototype.initEvent) {
      const event = document.createEvent('Event');
      event.initEvent(eventName, true, true);
      return event;
    }
    return new window.Event(eventName);
  }

  goToUrl(newUrl) {
    if (!newUrl || hasNoWindow() || newUrl === window.location.href) return;

    if (this.props.renderMode === PUBLISH) {
      window.location.assign(newUrl);
    } else {
      window.history.pushState({}, null, newUrl);
      window.dispatchEvent(this.generateNavigateEvent());
    }
  }

  filterCategory(category) {
    const oldCategory = this.getSelectedCategory();
    let url = setCategoryParam(category);

    if (!category) url = setAllPostsParam(url);
    if (oldCategory === category && category !== null) return;

    this.goToUrl(url);
  }

  navigate(newSlug) {
    if (hasNoWindow()) return;

    const { renderMode, currentPageRoute } = this.props;

    let newUrl;
    if (renderMode === PUBLISH) {
      newUrl = newSlug ? buildPostPath(newSlug, currentPageRoute) : setAllPostsParam(getPagePath());
    } else {
      newUrl = setSlugParam(newSlug);
    }

    this.goToUrl(newUrl);
  }

  render() {
    return (
      <this.RssFeeds
        slug={ this.state.slug || '' }
        navigate={ this.navigate }
        imgParams={ this.imgParams }
        filterCategory={ this.filterCategory }
        selectedCategory={ this.state.selectedCategory || '' }
        onFeedLoaded={ this.scrollIntoView }
        { ...this.props }
      />
    );
  }
}

export default Router;
